import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axiosInstance from './axiosInstance';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, ArrowDownward, ArrowUpward, DeleteOutline, EditOutlined, Search } from '@mui/icons-material';
import './ExportGridView.css';

const payrollColumnWidths = {
  job_title: '32%',
  payroll_reference: '22%',
  salary: '18%',
  rate: '14%',
  actions: '14%',
};

const defaultFormState = {
  job_title: '',
  payroll_reference: '',
  salary: '',
  rate: 'Hourly',
};

const ManagePayrollPage = () => {
  const [records, setRecords] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [loadingJobTitles, setLoadingJobTitles] = useState(false);
  const [savingRecord, setSavingRecord] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [pageInput, setPageInput] = useState('1');
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortKey, setSortKey] = useState('job_title');
  const [sortDirection, setSortDirection] = useState('asc');
  const [jobTitleSearch, setJobTitleSearch] = useState('');
  const [payrollReferenceSearch, setPayrollReferenceSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [originalPayrollReference, setOriginalPayrollReference] = useState('');
  const [formData, setFormData] = useState(defaultFormState);
  const [formError, setFormError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingPayrollReference, setDeletingPayrollReference] = useState('');

  const showToast = useCallback((message, severity = 'success') => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('hv-toast', { detail: { message, severity } }));
  }, []);

  const sortedJobTitles = useMemo(
    () => [...jobTitles].sort((left, right) => (left?.label || '').localeCompare(right?.label || '')),
    [jobTitles],
  );

  const loadJobTitles = useCallback(async () => {
    setLoadingJobTitles(true);
    try {
      const response = await axiosInstance.get('/api/v1/manage/job-titles', {
        params: { page: 1, limit: 1000, sort_key: 'job_title', sort_direction: 'asc' },
      });
      const payload = response?.data?.data ?? [];
      setJobTitles(
        (Array.isArray(payload) ? payload : []).map((entry) => ({
          value: entry?.normalized_job_title || '',
          label: entry?.job_title || entry?.normalized_job_title || '',
        })).filter((entry) => entry.value && entry.label),
      );
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Failed to load job titles.', 'error');
    } finally {
      setLoadingJobTitles(false);
    }
  }, [showToast]);

  const hasActiveFilters = Boolean(jobTitleSearch.trim() || payrollReferenceSearch.trim());

  const loadPayrollRecords = useCallback(async (
    pageIndex = page,
    nextSortKey = sortKey,
    nextSortDirection = sortDirection,
    nextJobTitleSearch = jobTitleSearch,
    nextPayrollReferenceSearch = payrollReferenceSearch,
  ) => {
    setLoadingRecords(true);
    try {
      const response = await axiosInstance.get('/api/v1/manage/payroll', {
        params: {
          page: pageIndex + 1,
          limit: rowsPerPage,
          sort_key: nextSortKey,
          sort_direction: nextSortDirection,
          ...(nextJobTitleSearch.trim() ? { job_title: nextJobTitleSearch.trim() } : {}),
          ...(nextPayrollReferenceSearch.trim() ? { payroll_reference: nextPayrollReferenceSearch.trim() } : {}),
        },
      });
      const payload = response?.data ?? {};
      const nextRecords = Array.isArray(payload?.data) ? payload.data : [];
      setRecords(nextRecords);
      setTotalRecords(Number(payload?.total ?? nextRecords.length ?? 0));
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Failed to load payroll records.', 'error');
    } finally {
      setLoadingRecords(false);
    }
  }, [jobTitleSearch, page, payrollReferenceSearch, rowsPerPage, showToast, sortDirection, sortKey]);

  useEffect(() => {
    loadJobTitles();
  }, [loadJobTitles]);

  useEffect(() => {
    loadPayrollRecords(page, sortKey, sortDirection, jobTitleSearch, payrollReferenceSearch);
  }, [jobTitleSearch, loadPayrollRecords, page, payrollReferenceSearch, sortKey, sortDirection]);

  useEffect(() => {
    setPageInput(String(page + 1));
  }, [page, totalRecords]);

  const closeDialog = () => {
    if (savingRecord) return;
    setDialogOpen(false);
    setFormError('');
    setFormData(defaultFormState);
    setFormMode('create');
    setOriginalPayrollReference('');
  };

  const openDeleteDialog = (record) => {
    setDeleteTarget(record);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deletingPayrollReference) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const openCreateDialog = () => {
    setFormMode('create');
    setOriginalPayrollReference('');
    setFormData(defaultFormState);
    setFormError('');
    setDialogOpen(true);
  };

  const openEditDialog = (record) => {
    setFormMode('edit');
    setOriginalPayrollReference(record?.payroll_reference || '');
    setFormData({
      job_title: record?.job_title || '',
      payroll_reference: record?.payroll_reference || '',
      salary: record?.salary ?? '',
      rate: record?.rate || 'Hourly',
    });
    setFormError('');
    setDialogOpen(true);
  };

  const handleFormChange = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.job_title.trim()) return 'Job Title is required.';
    if (!formData.payroll_reference.trim()) return 'Payroll Reference is required.';
    if (formData.salary === '' || formData.salary === null || formData.salary === undefined) return 'Salary is required.';
    return '';
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSavingRecord(true);
    setFormError('');
    try {
      const payload = {
        job_title: formData.job_title.trim(),
        payroll_reference: formData.payroll_reference.trim(),
        salary: formData.salary,
        rate: formData.rate,
      };

      if (formMode === 'edit' && originalPayrollReference) {
        await axiosInstance.put(`/api/v1/manage/payroll/${encodeURIComponent(originalPayrollReference)}`, payload);
      } else {
        await axiosInstance.post('/api/v1/manage/payroll', payload);
      }

      showToast(formMode === 'edit' ? 'Payroll record updated successfully.' : 'Payroll record created successfully.', 'success');
      closeDialog();
      await loadPayrollRecords(page, sortKey, sortDirection, jobTitleSearch, payrollReferenceSearch);
    } catch (error) {
      const message = error?.response?.data?.error || error?.message || 'Failed to save payroll record.';
      setFormError(message);
      showToast(message, 'error');
    } finally {
      setSavingRecord(false);
    }
  };

  const handleDelete = async () => {
    const payrollReference = deleteTarget?.payroll_reference || '';
    if (!payrollReference) return;

    setDeletingPayrollReference(payrollReference);
    try {
      await axiosInstance.delete(`/api/v1/manage/payroll/${encodeURIComponent(payrollReference)}`);
      showToast('Payroll record deleted successfully.', 'success');
      closeDeleteDialog();
      await loadPayrollRecords(page, sortKey, sortDirection, jobTitleSearch, payrollReferenceSearch);
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Failed to delete payroll record.', 'error');
    } finally {
      setDeletingPayrollReference('');
    }
  };

  const handlePageChange = (_event, newPage) => {
    if (newPage === page) return;
    setPage(newPage);
  };

  const handlePageInputChange = (event) => {
    const numericValue = event.target.value.replace(/[^0-9]/g, '');
    setPageInput(numericValue);
  };

  const handlePageInputKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (!totalRecords) {
      setPageInput('1');
      return;
    }
    const desiredPage = Number(pageInput);
    if (!Number.isFinite(desiredPage) || desiredPage < 1) {
      setPageInput(String(page + 1));
      return;
    }
    const totalPages = Math.max(Math.ceil(totalRecords / rowsPerPage), 1);
    const clampedPage = Math.min(Math.max(Math.floor(desiredPage), 1), totalPages);
    if (clampedPage !== page + 1) {
      setPage(clampedPage - 1);
    } else {
      setPageInput(String(clampedPage));
    }
  };

  const handleSortChange = (columnKey) => {
    setSortKey((current) => {
      if (current === columnKey) {
        setSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortDirection('asc');
      }
      return columnKey;
    });
    setPage(0);
  };

  const handleJobTitleSearchChange = (event) => {
    setJobTitleSearch(event.target.value);
    setPage(0);
  };

  const handlePayrollReferenceSearchChange = (event) => {
    setPayrollReferenceSearch(event.target.value);
    setPage(0);
  };

  const clearFilters = () => {
    setJobTitleSearch('');
    setPayrollReferenceSearch('');
    setPage(0);
  };

  const renderSortIcon = (columnKey) => {
    if (sortKey !== columnKey) return null;
    return sortDirection === 'asc' ? <ArrowUpward className="sortIcon" /> : <ArrowDownward className="sortIcon" />;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', textAlign: 'left' }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ mb: 0.5, fontWeight: 500 }}>
              Payroll
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
            <TextField
              size="small"
              label="Search Job Title"
              value={jobTitleSearch}
              onChange={handleJobTitleSearchChange}
              sx={{ minWidth: 260 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              size="small"
              label="Search Payroll Reference"
              value={payrollReferenceSearch}
              onChange={handlePayrollReferenceSearchChange}
              sx={{ minWidth: 220 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              sx={{ minWidth: 130, whiteSpace: 'nowrap' }}
            >
              Clear Filters
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
              Add Payroll
            </Button>
          </Stack>
        </Box>

        <TableContainer
          sx={{ position: 'relative', maxHeight: '68vh', minHeight: loadingRecords && !records.length ? 200 : 'auto', mt: 2, overflowX: 'auto', overflowY: 'auto' }}
        >
          {loadingRecords && records.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.82)',
                backdropFilter: 'blur(1px)',
                pointerEvents: 'none',
                zIndex: 3,
              }}
            >
              <CircularProgress size={48} thickness={4} />
              <Typography variant="body2" color="text.secondary">
                Loading next page...
              </Typography>
            </Box>
          )}
          <Table
            stickyHeader
            size="small"
            sx={{
              '& .MuiTableCell-sizeSmall': {
                padding: '8px 16px',
                height: 44,
                boxSizing: 'border-box',
              },
              '& .MuiTableHead .MuiTableCell-sizeSmall': {
                padding: '12px 16px',
                height: 48,
              },
              '& .MuiInputBase-root.MuiInputBase-sizeSmall': {
                minHeight: 28,
              },
              '& .MuiSelect-select.MuiSelect-select': {
                paddingTop: '4px',
                paddingBottom: '4px',
              },
              '& .MuiButton-sizeSmall': {
                minHeight: 28,
                paddingTop: 0,
                paddingBottom: 0,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell className={`sortableHeader ${sortKey === 'job_title' ? 'sorted' : ''}`} sx={{ width: payrollColumnWidths.job_title, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent" onClick={() => handleSortChange('job_title')} role="button" tabIndex={0}>
                    <strong>Job Title</strong>
                    {renderSortIcon('job_title')}
                  </div>
                </TableCell>
                <TableCell className={`sortableHeader ${sortKey === 'payroll_reference' ? 'sorted' : ''}`} sx={{ width: payrollColumnWidths.payroll_reference, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent" onClick={() => handleSortChange('payroll_reference')} role="button" tabIndex={0}>
                    <strong>Payroll Reference</strong>
                    {renderSortIcon('payroll_reference')}
                  </div>
                </TableCell>
                <TableCell className={`sortableHeader ${sortKey === 'salary' ? 'sorted' : ''}`} sx={{ width: payrollColumnWidths.salary, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent" onClick={() => handleSortChange('salary')} role="button" tabIndex={0}>
                    <strong>Salary</strong>
                    {renderSortIcon('salary')}
                  </div>
                </TableCell>
                <TableCell className={`sortableHeader ${sortKey === 'rate' ? 'sorted' : ''}`} sx={{ width: payrollColumnWidths.rate, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent" onClick={() => handleSortChange('rate')} role="button" tabIndex={0}>
                    <strong>Rate</strong>
                    {renderSortIcon('rate')}
                  </div>
                </TableCell>
                <TableCell className="sortableHeader" sx={{ width: payrollColumnWidths.actions, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent">
                    <strong>Actions</strong>
                  </div>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingRecords && !records.length ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                      <CircularProgress size={18} thickness={4} />
                      <Typography color="text.secondary">Loading...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : records.length > 0 ? (
                records.map((record) => (
                  <TableRow key={record?.payroll_reference} hover>
                    <TableCell>{record?.job_title || '-'}</TableCell>
                    <TableCell>{record?.payroll_reference || '-'}</TableCell>
                    <TableCell>{Number.isFinite(Number(record?.salary)) ? `£${Number(record.salary).toFixed(2)}` : '-'}</TableCell>
                    <TableCell>{record?.rate || '-'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <IconButton size="small" color="primary" onClick={() => openEditDialog(record)} aria-label="edit payroll record">
                          <EditOutlined fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => openDeleteDialog(record)} aria-label="delete payroll record">
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    No payroll records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <TextField
              value={pageInput}
              onChange={handlePageInputChange}
              onKeyDown={handlePageInputKeyDown}
              label="Page"
              size="small"
              sx={{ width: 90 }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              disabled={loadingRecords || !totalRecords}
            />
            <Typography variant="body2" color="text.secondary">
              {totalRecords ? `of ${Math.max(Math.ceil(totalRecords / rowsPerPage), 1)}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalRecords ? `Showing ${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, totalRecords)} of ${totalRecords}` : 'No records to show'}
            </Typography>
          </Box>
          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
            showFirstButton
            showLastButton
            labelRowsPerPage=""
            labelDisplayedRows={() => ''}
            sx={{
              '.MuiTablePagination-toolbar': { p: 0 },
              '.MuiTablePagination-selectLabel': { display: 'none' },
              '.MuiTablePagination-displayedRows': { m: 0 },
            }}
          />
        </Box>
      </Paper>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{formMode === 'edit' ? 'Edit Payroll Record' : 'Add Payroll Record'}</DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="payroll-job-title-label">Job Title</InputLabel>
              <Select
                labelId="payroll-job-title-label"
                value={formData.job_title}
                label="Job Title"
                onChange={(event) => handleFormChange('job_title', event.target.value)}
                disabled={loadingJobTitles}
              >
                <MenuItem value="" disabled>
                  <em>Select Job Title</em>
                </MenuItem>
                {formData.job_title && !sortedJobTitles.some((jobTitle) => jobTitle.label === formData.job_title) && (
                  <MenuItem value={formData.job_title} disabled>
                    {formData.job_title}
                  </MenuItem>
                )}
                {sortedJobTitles.map((jobTitle) => (
                  <MenuItem key={jobTitle.value} value={jobTitle.label}>
                    {jobTitle.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Payroll Reference"
              value={formData.payroll_reference}
              onChange={(event) => handleFormChange('payroll_reference', event.target.value)}
              fullWidth
            />
            <TextField
              label="Salary"
              value={formData.salary}
              onChange={(event) => handleFormChange('salary', event.target.value)}
              fullWidth
              type="number"
            />
            <TextField
              label="Rate"
              value={formData.rate}
              onChange={(event) => handleFormChange('rate', event.target.value)}
              fullWidth
            />
            {formError && (
              <Typography variant="body2" color="error">
                {formError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={savingRecord}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={savingRecord}>
            {savingRecord ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Delete Payroll Record</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete payroll record {deleteTarget?.payroll_reference || '-'}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={Boolean(deletingPayrollReference)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={Boolean(deletingPayrollReference)}>
            {deletingPayrollReference ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManagePayrollPage;
